<template>
  <div class="container">

    <h1 class="se-bp-title">Additional Information: {{ getWageEmploymentLabel(employmentIdx) }}</h1>

    <template v-if="currentEmployment">

      <el-collapse v-model="activePanels">


        <!-- ================= Pay Increase Information ================= -->
        <el-collapse-item name="payIncreaseInformation">
          <template #title>
            <div>
              <div class="se-panel-title">Pay Increase Information</div>
              <div class="se-panel-subtitle">Most recent and next pay increase date, amount, and frequency.</div>
            </div>
          </template>

          <h4 class="mt-2">Pay Increase</h4>
          <div class="row">
            <div class="col-md-3">
              <label class="bp-label">Most Recent Pay Increase Date</label>
              <el-date-picker size="large" v-model="payIncreaseMostRecentDate" type="date" value-format="MM/DD/YYYY" format="MM/DD/YYYY" :disabled="isLocked" clearable placeholder="Select date"/>
            </div>
            <div class="col-md-3">
              <label class="bp-label">Most Recent Pay Increase Amount</label>
              <input class="bp-form-input bp-u-shadow-04" type="text" :readonly="isLocked" v-mask="currencyMask" v-model="payIncreaseMostRecentAmount"/>
            </div>
            <div class="col-md-3">
              <label class="bp-label">Most Recent Pay Increase Frequency</label>
              <el-select size="large" v-model="payIncreaseMostRecentFrequency" :disabled="isLocked" clearable placeholder="Select">
                <el-option v-for="opt in payFrequencyOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
              </el-select>
            </div>
            <div class="col-md-3">
              <label class="bp-label">Times Per Year
                <a class="btn-link se-help-icon" v-tooltip.top="'Learn More'" @click="showMostRecentFrequencyTimesPerYearHelp = true">
                  <i class="fa fa-info-circle"></i>
                </a>
              </label>
              <fg-input type="number" :readonly="isLocked" v-model.number="payIncreaseMostRecentFrequencyTimesPerYear"></fg-input>
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-md-3">
              <label class="bp-label">Next Pay Increase Date</label>
              <el-date-picker size="large" v-model="payIncreaseNextDate" type="date" value-format="MM/DD/YYYY" format="MM/DD/YYYY" :disabled="isLocked" clearable placeholder="Select date"/>
            </div>
            <div class="col-md-3">
              <label class="bp-label">Next Pay Increase Amount</label>
              <input class="bp-form-input bp-u-shadow-04" type="text" :readonly="isLocked" v-mask="currencyMask" v-model="payIncreaseNextAmount"/>
            </div>
            <div class="col-md-3">
              <label class="bp-label">Next Pay Increase Frequency</label>
              <el-select size="large" v-model="payIncreaseNextFrequency" :disabled="isLocked" clearable placeholder="Select">
                <el-option v-for="opt in payFrequencyOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
              </el-select>
            </div>
            <div class="col-md-3">
              <label class="bp-label">Times Per Year
                <a class="btn-link se-help-icon" v-tooltip.top="'Learn More'" @click="showNextFrequencyTimesPerYearHelp = true">
                  <i class="fa fa-info-circle"></i>
                </a>
              </label>
              <fg-input type="number" :readonly="isLocked" v-model.number="payIncreaseNextFrequencyTimesPerYear"></fg-input>
            </div>
          </div>
        </el-collapse-item>

        <!-- ================= Employment History Details ================= -->
        <el-collapse-item name="employmentHistoryDetails">
          <template #title>
            <div>
              <div class="se-panel-title">Employment history details</div>
              <div class="se-panel-subtitle">Start date, length of employment, prior history.</div>
            </div>
          </template>

          <div class="row mt-2">
            <div class="col-md-4">
              <fg-input label="Time in Line of Work (months)" type="number" :readonly="isLocked" v-model.number="timeInLineOfWorkMonths"></fg-input>
            </div>
            <div class="col-md-4">
              <fg-input label="Employment Position" type="text" :readonly="isLocked" v-model="employmentPosition"></fg-input>
            </div>
          </div>

          <div class="row mt-2">
            <div class="col-md-4"><el-checkbox v-model="employmentHistoryMeetsGuidelinesIndicator" :disabled="isLocked">Employment History Meets Guidelines</el-checkbox></div>
            <div class="col-md-4"><el-checkbox v-model="employmentHistoryGapIndicator" :disabled="isLocked">Employment History Gap</el-checkbox></div>
          </div>
        </el-collapse-item>

      </el-collapse>
    </template>

    <se-help v-model:visible="showMostRecentFrequencyTimesPerYearHelp" title="Learn More">
      <p>Describes the number of times in calendar year the borrower earns the amount defined as most recent pay increase frequency. e.g. 20</p>
    </se-help>

    <se-help v-model:visible="showNextFrequencyTimesPerYearHelp" title="Learn More">
      <p>Describes the number of times in calendar year the borrower earns the amount defined as next pay increase frequency. e.g. 20</p>
    </se-help>

    <se-buttons
      :display-previous="true"
      :display-save="false"
      :display-submit="false"
      @seNext="next"
      @sePrevious="previous">
    </se-buttons>

  </div>
</template>
<script>
import { defineComponent } from 'vue';
import {mapGetters} from 'vuex'
import {ElSelect, ElOption, ElDatePicker, ElCheckbox, ElCollapse, ElCollapseItem} from 'element-plus'
import SeButtons from './SeButtons.vue'
import SeHelp from './SeHelp.vue'
import wageFormMixin from '@/mixins/WageFormMixin'
import Enums from 'src/model/enums'
import {
  SET_WAGE_EMPLOYMENT_DATA
} from '@/store/actions/se'

function toOptions(valuesEnum, labelsEnum) {
  return Object.keys(valuesEnum).map(key => ({value: valuesEnum[key], label: labelsEnum[key]}))
}


export default defineComponent({
  mixins: [wageFormMixin],

  components: {
    SeButtons,
    SeHelp,
    [ElSelect.name]: ElSelect,
    [ElOption.name]: ElOption,
    [ElDatePicker.name]: ElDatePicker,
    [ElCheckbox.name]: ElCheckbox,
    [ElCollapse.name]: ElCollapse,
    [ElCollapseItem.name]: ElCollapseItem
  },

  data() {
    return {
      activePanels: [],

      showMostRecentFrequencyTimesPerYearHelp: false,
      showNextFrequencyTimesPerYearHelp: false,

      monthOptions: toOptions(Enums.SeEmployeePeriodMonthType, Enums.SeEmployeePeriodMonthTypeLabel),
      payFrequencyOptions: toOptions(Enums.SeEmployeePayFrequencyType, Enums.SeEmployeePayFrequencyTypeLabel)
    }
  },


  computed: {
    ...mapGetters(['getWageEmploymentLabel']),

    employmentHistoryMeetsGuidelinesIndicator: { get() { return this.currentEmployment?.employmentHistoryMeetsGuidelinesIndicator }, set(v) { this.setEmploymentField('employmentHistoryMeetsGuidelinesIndicator', v) } },
    employmentHistoryGapIndicator: { get() { return this.currentEmployment?.employmentHistoryGapIndicator }, set(v) { this.setEmploymentField('employmentHistoryGapIndicator', v) } },
    timeInLineOfWorkMonths: { get() { return this.currentEmployment?.timeInLineOfWorkMonths }, set(v) { this.setEmploymentField('timeInLineOfWorkMonths', v) } },
    employmentPosition: { get() { return this.currentEmployment?.employmentPosition }, set(v) { this.setEmploymentField('employmentPosition', v) } },
    payIncreaseNextDate: { get() { return this.currentEmployment?.payIncreaseNextDate }, set(v) { this.setEmploymentField('payIncreaseNextDate', v) } },
    payIncreaseMostRecentDate: { get() { return this.currentEmployment?.payIncreaseMostRecentDate }, set(v) { this.setEmploymentField('payIncreaseMostRecentDate', v) } },
    payIncreaseMostRecentAmount: { get() { return this.currentEmployment?.payIncreaseMostRecentAmount }, set(v) { this.setEmploymentField('payIncreaseMostRecentAmount', this.parseCurrencyToNumber(v)) } },
    payIncreaseMostRecentFrequency: { get() { return this.currentEmployment?.payIncreaseMostRecentFrequency }, set(v) { this.setEmploymentField('payIncreaseMostRecentFrequency', v) } },
    payIncreaseMostRecentFrequencyTimesPerYear: { get() { return this.currentEmployment?.payIncreaseMostRecentFrequencyTimesPerYear }, set(v) { this.setEmploymentField('payIncreaseMostRecentFrequencyTimesPerYear', v) } },
    payIncreaseNextAmount: { get() { return this.currentEmployment?.payIncreaseNextAmount }, set(v) { this.setEmploymentField('payIncreaseNextAmount', this.parseCurrencyToNumber(v)) } },
    payIncreaseNextFrequency: { get() { return this.currentEmployment?.payIncreaseNextFrequency }, set(v) { this.setEmploymentField('payIncreaseNextFrequency', v) } },
    payIncreaseNextFrequencyTimesPerYear: { get() { return this.currentEmployment?.payIncreaseNextFrequencyTimesPerYear }, set(v) { this.setEmploymentField('payIncreaseNextFrequencyTimesPerYear', v) } }
  },

  methods: {
    setEmploymentField(key, value) {
      this.$store.commit(SET_WAGE_EMPLOYMENT_DATA, {employmentIdx: this.employmentIdx, fields: {[key]: value}})
    }
  },
});
</script>
<style scoped>

.se-panel-title {
  font-weight: 600;
  font-size: 1.125rem;
}

.se-panel-subtitle {
  font-weight: 400;
  color: rgba(0, 0, 0, .6);
  font-size: .875rem;
}

/* Keep the fields horizontally aligned across a row even when some labels
   have the "Learn More" info icon and some don't (which can make labels
   slightly taller/shorter than their neighbors). Give every label a fixed
   reserved height so the input/select controls that follow always start
   at the same vertical position. */
:deep(.row) > [class*="col-"] {
  display: flex;
  flex-direction: column;
}

:deep(.bp-label) {
  display: flex;
  align-items: flex-end;
  min-height: 34px;
  margin: 0 0 12px 0;
  font-weight: 600;
}

:deep(.bp-form-item) {
  display: flex;
  flex-direction: column;
}

:deep(.bp-form-item) > .bp-form-label {
  display: flex;
  align-items: flex-end;
  min-height: 34px;
  margin: 0 0 12px 0;
}

:deep(.el-select),
:deep(.el-date-editor.el-input) {
  width: 100%;
}

:deep(.el-select__wrapper),
:deep(.el-input__wrapper) {
  min-height: 42px;
  padding: 8px 12px;
  border-radius: 4px;
  box-shadow: none !important;
  border: 1px solid rgba(0, 0, 0, .4);
  background-color: #fff;
}

:deep(.el-select__wrapper.is-hovering:not(.is-focused)),
:deep(.el-input__wrapper:hover) {
  border-color: rgba(0, 0, 0, .4);
}

:deep(.el-select__wrapper.is-focused),
:deep(.el-input__wrapper.is-focus) {
  border-color: #1887cc;
  box-shadow: 0 0 0 1px rgba(24, 135, 204, .4) !important;
}

:deep(.el-select__selection),
:deep(.el-input__inner) {
  font-size: 1rem;
  line-height: 24px;
  height: 24px;
  color: #121212;
}

:deep(.el-select__placeholder),
:deep(.el-input__inner::placeholder) {
  color: rgba(0, 0, 0, .75);
}

:deep(.el-checkbox__label) {
  font-size: 1rem;
}

:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background-color: #0a649d;
  border-color: #0a649d;
}

:deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
  color: #0a649d;
}

:deep(.el-checkbox__input.is-focus .el-checkbox__inner) {
  border-color: #0a649d;
}

:deep(.el-checkbox__input .el-checkbox__inner:hover) {
  border-color: #0a649d;
}
</style>

